import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  CAMPAIGN_STATUS_FILTERS,
  type CampaignStatusFilter as StatusFilter,
} from '@/lib/campaign-list';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

/**
 * Todas · Agendadas · Enviadas · Rascunhos (PUSH-01 AC 1, PUSH-02 AC 3/6):
 * pill chips below 768px (scrolling only inside the group), the
 * `SegmentedToggle` from 768px up (AD-016).
 */
export function CampaignStatusFilter({
  value,
  onChange,
  className,
}: {
  value: StatusFilter;
  onChange(value: StatusFilter): void;
  className?: string;
}) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const label: Record<StatusFilter, string> = {
    all: LL.campaigns.statusFilter.all(),
    scheduled: LL.campaigns.statusFilter.scheduled(),
    sent: LL.campaigns.statusFilter.sent(),
    draft: LL.campaigns.statusFilter.draft(),
  };

  if (!isMobile) {
    return (
      <SegmentedToggle
        value={value}
        options={CAMPAIGN_STATUS_FILTERS}
        getLabel={(filter) => label[filter]}
        onChange={onChange}
        ariaLabel={LL.campaigns.statusFilter.label()}
        className={className}
      />
    );
  }

  return (
    <div
      role="group"
      aria-label={LL.campaigns.statusFilter.label()}
      className={cn(
        '-mx-1 flex min-w-0 max-w-full gap-2 overflow-x-auto px-1 py-0.5 [scrollbar-width:none]',
        className
      )}
    >
      {CAMPAIGN_STATUS_FILTERS.map((filter) => {
        const pressed = value === filter;
        return (
          <button
            key={filter}
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(filter)}
            className={cn(
              'inline-flex min-h-touch shrink-0 items-center whitespace-nowrap border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mobileLayer.chip,
              pressed
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background text-foreground hover:bg-accent'
            )}
          >
            {label[filter]}
          </button>
        );
      })}
    </div>
  );
}
